import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserContext } from './UserContext';
import { useTimelineContext } from './TimelineContext';

const TutorialContext = createContext();

const BEGINNER_TUTORIAL_STATUS_PREFIX = 'beginnerTutorialStatus_';

export const TUTORIAL_STATUSES = Object.freeze({
  COMPLETED: 'completed',
  SKIPPED: 'skipped'
});

export const getBeginnerTutorialStatusKey = (userId) => `${BEGINNER_TUTORIAL_STATUS_PREFIX}${userId}`;

export const userHasTimelineWithEvents = (timelines = []) => {
  return timelines.some(timeline => Array.isArray(timeline.events) && timeline.events.length > 0);
};

export const shouldAutoStartBeginnerTutorial = ({ status, userTimelines }) => {
  return !status && !userHasTimelineWithEvents(userTimelines);
};

export const getNewTutorialEventId = (events = [], eventIdsAtStart = null) => {
  if (eventIdsAtStart) {
    const createdEvent = events.find(event => !eventIdsAtStart.has(event.id));
    if (createdEvent) return createdEvent.id;
  }

  return events.length > 0 ? events[events.length - 1].id : null;
};

const getGalleryStartStep = (userTimelines) => (
  userTimelines.length > 0 ? 'gallery-open' : 'gallery-create'
);

const getStartStepForPath = (pathname, userTimelines) => (
  pathname.startsWith('/timeline/') ? 'timeline-add' : getGalleryStartStep(userTimelines)
);

const isGalleryPath = (pathname) => pathname.startsWith('/gallery');
const isTimelinePath = (pathname) => pathname.startsWith('/timeline/');

export const TUTORIAL_TARGET_MODES = Object.freeze({
  ACTION: 'action',
  FORM_TASK: 'form-task',
  PRACTICE: 'practice',
  REVIEW: 'review',
  INFO: 'info',
  COMPLETION: 'completion'
});

export const getRouteRecoveryForTutorialStep = (stepId, pathname) => {
  const step = BEGINNER_TUTORIAL_STEPS[stepId];
  if (!step) return null;

  if (stepId === 'gallery-return' && isGalleryPath(pathname)) {
    return {
      stepId: 'gallery-saved-timeline',
      notice: ''
    };
  }

  if (step.screen === 'timeline' && isGalleryPath(pathname)) {
    return {
      stepId: 'gallery-open',
      notice: 'You returned to the gallery. Open your timeline when you are ready to continue the tutorial.'
    };
  }

  if (step.screen === 'gallery' && isTimelinePath(pathname)) {
    return {
      stepId: 'timeline-created',
      notice: ''
    };
  }

  return null;
};

export const BEGINNER_TUTORIAL_WELCOME = Object.freeze({
  title: 'Welcome to Agora Timeline',
  body: 'Agora Timeline helps you build clear, visual history timelines with events, dates, images, and notes.',
  question: 'Would you like a quick guided tutorial? It takes about two minutes and walks you through the main features.',
  startLabel: 'Start tutorial',
  skipLabel: 'Not now'
});

export const BEGINNER_TUTORIAL_SKIPPED_MESSAGE = Object.freeze({
  title: 'Tutorial skipped',
  body: 'You can start the beginner tutorial anytime from the Menu when you are inside a timeline. The Menu also includes export/import tools, which are useful for keeping backups while the app is in beta.',
  closeLabel: 'Got it'
});

export const BEGINNER_TUTORIAL_STEPS = Object.freeze({
  'gallery-create': {
    screen: 'gallery',
    targetMode: TUTORIAL_TARGET_MODES.FORM_TASK,
    targetSelector: '[data-tutorial-id="new-timeline-form"]',
    initialFocusSelector: '#new-timeline-name',
    title: 'Start in your gallery',
    body: 'This is where your timelines live. Create a timeline to begin.',
    instruction: 'Name your timeline and click Create.'
  },
  'gallery-open': {
    screen: 'gallery',
    targetMode: TUTORIAL_TARGET_MODES.ACTION,
    targetSelector: '[data-tutorial-id="open-timeline"]',
    title: 'Open a timeline',
    body: 'Your gallery shows saved timelines for this user ID.',
    instruction: 'Open a timeline to continue.'
  },
  'timeline-created': {
    screen: 'timeline',
    targetMode: TUTORIAL_TARGET_MODES.REVIEW,
    targetSelector: '[data-tutorial-id="timeline-title"]',
    title: 'You are inside your timeline',
    body: 'The timeline you opened is shown here by name. This is the workspace where its events will appear.',
    instruction: 'Click Next to add your first event.',
    nextLabel: 'Next',
    nextStep: 'timeline-add'
  },
  'timeline-add': {
    screen: 'timeline',
    targetMode: TUTORIAL_TARGET_MODES.ACTION,
    targetSelector: '.timeline-quick-add-button',
    title: 'Add your first event',
    body: 'Events are the building blocks of a timeline.',
    instruction: 'Click Create Event.',
    advanceOnTargetClick: true,
    nextStep: 'event-form'
  },
  'event-form': {
    screen: 'timeline',
    targetMode: TUTORIAL_TARGET_MODES.FORM_TASK,
    targetSelector: '.event-form',
    spotlightSelector: '[data-tutorial-id="event-form-core"]',
    secondarySpotlightSelector: '[data-tutorial-id="event-form-submit"]',
    initialFocusSelector: '#event-title',
    title: 'Create one event',
    body: 'Add a title, then adjust the date so the event lands where you expect on the timeline.',
    instruction: 'Enter a title, change the date, and click Create.',
    missingTargetStep: 'timeline-add',
    missingTargetNotice: 'The event form was closed. Click Create Event when you are ready to continue.'
  },
  'event-created': {
    screen: 'timeline',
    targetMode: TUTORIAL_TARGET_MODES.REVIEW,
    targetSelector: '.timeline-event.point .event-content',
    title: 'Your event is on the timeline',
    body: 'The event you created now appears as a card connected to its date on the timeline.',
    instruction: 'Click Next to learn how to move around.',
    blockTargetInteraction: true,
    nextLabel: 'Next',
    nextStep: 'navigate'
  },
  navigate: {
    screen: 'timeline',
    targetMode: TUTORIAL_TARGET_MODES.PRACTICE,
    targetSelector: '.timeline-main',
    title: 'Move through time',
    body: 'Drag the timeline to pan. Scroll or pinch to zoom in and out.',
    instruction: 'Try moving around, then continue.',
    nextLabel: 'Next',
    nextStep: 'event-open'
  },
  'event-open': {
    screen: 'timeline',
    targetMode: TUTORIAL_TARGET_MODES.ACTION,
    targetSelector: '.timeline-event.point .event-content',
    title: 'Open event details',
    body: 'Click an event to read it without accidentally editing it.',
    instruction: 'Click your event.',
    advanceOnTargetClick: true,
    nextStep: 'event-edit'
  },
  'event-edit': {
    screen: 'timeline',
    targetMode: TUTORIAL_TARGET_MODES.ACTION,
    targetSelector: '[data-tutorial-id="edit-event"]',
    title: 'Edit when needed',
    body: 'Use Edit Event when you want to revise the title, date, description, image, tags, or position.',
    instruction: 'Click Edit Event.',
    advanceOnTargetClick: true,
    nextStep: 'event-edit-result',
    missingTargetStep: 'event-open',
    missingTargetNotice: 'The event details closed. Open your event again to continue.'
  },
  'event-edit-result': {
    screen: 'timeline',
    targetMode: TUTORIAL_TARGET_MODES.REVIEW,
    targetSelector: '.event-form',
    title: 'This is the edit form',
    body: 'After choosing Edit Event, the sidebar opens with the event details ready to revise. You can update changes or cancel to leave the event as it is.',
    instruction: 'Click Next to finish with the menu.',
    blockTargetInteraction: true,
    nextLabel: 'Next',
    nextStep: 'menu-button',
    missingTargetStep: 'event-open',
    missingTargetNotice: 'The edit form closed. Open your event again to continue.'
  },
  'menu-button': {
    screen: 'timeline',
    targetMode: TUTORIAL_TARGET_MODES.ACTION,
    targetSelector: '[data-tutorial-id="app-menu-button"]',
    title: 'Use the menu for help and backups',
    body: 'Your timelines are saved locally in this browser on this computer. Because Agora Timeline is still in beta, browser cleanup, device changes, or future updates could remove local data. Use Export after important work to keep a backup.',
    instruction: 'Open the menu.',
    advanceOnTargetClick: true,
    nextStep: 'menu-panel'
  },
  'menu-panel': {
    screen: 'timeline',
    targetMode: TUTORIAL_TARGET_MODES.REVIEW,
    targetSelector: '[data-tutorial-id="app-menu-panel"]',
    title: 'Replay or learn more',
    body: 'The menu includes backup tools, tutorial replay, and advanced help. Use it whenever you need to export work or review how the app works.',
    instruction: 'Click Next for one last look at the gallery.',
    blockTargetInteraction: true,
    hideSkip: true,
    nextLabel: 'Next',
    nextStep: 'gallery-return',
    missingTargetStep: 'menu-button',
    missingTargetNotice: 'The menu closed. Open the menu again to continue.'
  },
  'gallery-return': {
    screen: 'timeline',
    targetMode: TUTORIAL_TARGET_MODES.ACTION,
    targetSelector: '.home-link',
    title: 'Return to your gallery',
    body: 'The gallery is where your timelines are listed. Use the Agora Timeline button to go back there.',
    instruction: 'Click Agora Timeline.',
    advanceOnTargetClick: true,
    nextStep: 'gallery-saved-timeline'
  },
  'gallery-saved-timeline': {
    screen: 'gallery',
    targetMode: TUTORIAL_TARGET_MODES.COMPLETION,
    targetSelector: null,
    title: 'Your timeline is saved here',
    body: 'The timeline you created now appears as a card in your gallery. After you finish, use Open whenever you want to keep working on it.',
    instruction: 'You are ready to start working.',
    hideSkip: true,
    nextLabel: 'Finish',
    nextStep: null
  }
});

export const TutorialProvider = ({ children }) => {
  const location = useLocation();
  const { currentUser, userTimelines } = useUserContext();
  const { events } = useTimelineContext();
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [beginnerStepId, setBeginnerStepId] = useState(null);
  const [beginnerStepNotice, setBeginnerStepNotice] = useState('');
  const [tutorialStatusState, setTutorialStatusState] = useState({ key: null, status: null });
  const [tutorialEventId, setTutorialEventId] = useState(null);
  const eventCountAtEventFormStart = useRef(null);
  const eventIdsAtEventFormStart = useRef(null);

  const statusKey = currentUser?.id ? getBeginnerTutorialStatusKey(currentUser.id) : null;
  const isTutorialStatusLoaded = tutorialStatusState.key === statusKey;
  const tutorialStatus = isTutorialStatusLoaded ? tutorialStatusState.status : null;
  const beginnerStep = useMemo(() => {
    const step = beginnerStepId ? BEGINNER_TUTORIAL_STEPS[beginnerStepId] : null;
    if (!step) return null;
    const stepWithNotice = beginnerStepNotice
      ? { ...step, instruction: beginnerStepNotice }
      : step;
    if ((beginnerStepId === 'event-created' || beginnerStepId === 'event-open') && tutorialEventId !== null) {
      return {
        ...stepWithNotice,
        targetSelector: `[data-tutorial-event-id="${tutorialEventId}"] .event-content`,
        spotlightSelector: `[data-tutorial-event-id="${tutorialEventId}"] .event-content`
      };
    }
    return stepWithNotice;
  }, [beginnerStepId, beginnerStepNotice, tutorialEventId]);
  const isBeginnerTutorialActive = activeTutorial === 'beginner' && Boolean(beginnerStep);
  const isBeginnerTutorialWelcomeOpen = activeTutorial === 'beginner-welcome';
  const isBeginnerTutorialSkippedMessageOpen = activeTutorial === 'beginner-skipped-message';
  const isAdvancedPlaceholderOpen = activeTutorial === 'advanced';

  useEffect(() => {
    if (!statusKey) {
      setTutorialStatusState({ key: null, status: null });
      setActiveTutorial(null);
      setBeginnerStepId(null);
      return;
    }

    setTutorialStatusState({
      key: statusKey,
      status: window.localStorage.getItem(statusKey)
    });
  }, [statusKey]);

  const startBeginnerTutorial = useCallback((startStepId) => {
    eventCountAtEventFormStart.current = null;
    eventIdsAtEventFormStart.current = null;
    setTutorialEventId(null);
    setBeginnerStepNotice('');
    setActiveTutorial('beginner');
    setBeginnerStepId(startStepId || getStartStepForPath(location.pathname, userTimelines));
  }, [location.pathname, userTimelines]);

  const openBeginnerTutorialWelcome = useCallback(() => {
    setActiveTutorial('beginner-welcome');
    setBeginnerStepId(null);
    setBeginnerStepNotice('');
    setTutorialEventId(null);
  }, []);

  const openAdvancedTutorial = useCallback(() => {
    setActiveTutorial('advanced');
    setBeginnerStepId(null);
    setBeginnerStepNotice('');
    setTutorialEventId(null);
  }, []);

  const closeAdvancedTutorial = useCallback(() => {
    setActiveTutorial(null);
  }, []);

  const setBeginnerStatus = useCallback((status) => {
    if (statusKey) {
      window.localStorage.setItem(statusKey, status);
      setTutorialStatusState({ key: statusKey, status });
    }
  }, [statusKey]);

  const skipBeginnerTutorial = useCallback(() => {
    setBeginnerStatus(TUTORIAL_STATUSES.SKIPPED);
    setActiveTutorial('beginner-skipped-message');
    setBeginnerStepId(null);
    setBeginnerStepNotice('');
    setTutorialEventId(null);
  }, [setBeginnerStatus]);

  const closeBeginnerTutorialSkippedMessage = useCallback(() => {
    setActiveTutorial(null);
  }, []);

  const completeBeginnerTutorial = useCallback(() => {
    setBeginnerStatus(TUTORIAL_STATUSES.COMPLETED);
    setActiveTutorial(null);
    setBeginnerStepId(null);
    setBeginnerStepNotice('');
    setTutorialEventId(null);
  }, [setBeginnerStatus]);

  const goToBeginnerStep = useCallback((stepId, notice = '') => {
    if (!stepId) {
      completeBeginnerTutorial();
      return;
    }

    setBeginnerStepNotice(notice);
    setBeginnerStepId(stepId);
  }, [completeBeginnerTutorial]);

  const advanceBeginnerTutorial = useCallback(() => {
    if (!beginnerStep) return;
    goToBeginnerStep(beginnerStep.nextStep);
  }, [beginnerStep, goToBeginnerStep]);

  const recoverMissingTutorialTarget = useCallback((stepId) => {
    if (!isBeginnerTutorialActive || stepId !== beginnerStepId) return;
    const step = BEGINNER_TUTORIAL_STEPS[stepId];
    if (!step?.missingTargetStep) return;

    goToBeginnerStep(step.missingTargetStep, step.missingTargetNotice || '');
  }, [beginnerStepId, goToBeginnerStep, isBeginnerTutorialActive]);

  useEffect(() => {
    if (!currentUser || activeTutorial) return;
    if (!isTutorialStatusLoaded) return;
    if (!location.pathname.startsWith('/gallery') && !location.pathname.startsWith('/timeline/')) return;
    if (!shouldAutoStartBeginnerTutorial({ status: tutorialStatus, userTimelines })) return;

    openBeginnerTutorialWelcome();
  }, [activeTutorial, currentUser, isTutorialStatusLoaded, location.pathname, openBeginnerTutorialWelcome, tutorialStatus, userTimelines]);

  useEffect(() => {
    if (!isBeginnerTutorialActive) return;

    const recovery = getRouteRecoveryForTutorialStep(beginnerStepId, location.pathname);
    if (recovery) {
      setBeginnerStepNotice(recovery.notice);
      setBeginnerStepId(recovery.stepId);
    }
  }, [beginnerStepId, isBeginnerTutorialActive, location.pathname]);

  useEffect(() => {
    if (!isBeginnerTutorialActive) return;

    if (beginnerStepId === 'event-form' && eventCountAtEventFormStart.current === null) {
      eventCountAtEventFormStart.current = events.length;
      eventIdsAtEventFormStart.current = new Set(events.map(event => event.id));
    } else if (beginnerStepId !== 'event-form') {
      eventCountAtEventFormStart.current = null;
      eventIdsAtEventFormStart.current = null;
    }
  }, [beginnerStepId, events, events.length, isBeginnerTutorialActive]);

  useEffect(() => {
    if (!isBeginnerTutorialActive || beginnerStepId !== 'event-form') return;
    if (eventCountAtEventFormStart.current === null) return;

    if (events.length > eventCountAtEventFormStart.current) {
      setTutorialEventId(getNewTutorialEventId(events, eventIdsAtEventFormStart.current || new Set()));
      setBeginnerStepId('event-created');
    }
  }, [beginnerStepId, events, events.length, isBeginnerTutorialActive]);

  const value = useMemo(() => ({
    activeTutorial,
    beginnerStep,
    beginnerStepId,
    closeAdvancedTutorial,
    completeBeginnerTutorial,
    closeBeginnerTutorialSkippedMessage,
    goToBeginnerStep,
    isBeginnerTutorialSkippedMessageOpen,
    isBeginnerTutorialWelcomeOpen,
    isAdvancedPlaceholderOpen,
    isBeginnerTutorialActive,
    openBeginnerTutorialWelcome,
    openAdvancedTutorial,
    recoverMissingTutorialTarget,
    skipBeginnerTutorial,
    startBeginnerTutorial,
    advanceBeginnerTutorial,
    tutorialStatus
  }), [
    activeTutorial,
    advanceBeginnerTutorial,
    beginnerStep,
    beginnerStepId,
    closeAdvancedTutorial,
    closeBeginnerTutorialSkippedMessage,
    completeBeginnerTutorial,
    goToBeginnerStep,
    isAdvancedPlaceholderOpen,
    isBeginnerTutorialActive,
    isBeginnerTutorialSkippedMessageOpen,
    isBeginnerTutorialWelcomeOpen,
    openBeginnerTutorialWelcome,
    openAdvancedTutorial,
    recoverMissingTutorialTarget,
    skipBeginnerTutorial,
    startBeginnerTutorial,
    tutorialStatus
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorialContext = () => useContext(TutorialContext);
